import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsType } from "echarts/core";

import format from "@/services/Utils/format";

// The dashboard charts render with the SVG renderer, whose `getDataURL()` returns an
// SVG data URI. To produce a raster PNG (what users expect for pasting into docs) we
// re-render each chart's resolved option into a throwaway *canvas* instance and export
// from that. Registering CanvasRenderer here is required for the offscreen instance;
// `echarts.use` is idempotent so this is safe alongside the SVGRenderer the charts use.
echarts.use([CanvasRenderer]);

// Two kinds of dashboard figures can be exported:
//  - "echarts": the real ECharts charts (bar / line) — re-rendered to canvas.
//  - "segments": the SegmentBar composition figures (Emission by Env. Data Type / Model /
//    Embodied). These are plain DOM, not ECharts, so we draw them to canvas ourselves
//    rather than rasterise the DOM with html2canvas — the app's slate palette compiles to
//    oklch(), which html2canvas 1.4.1 cannot parse.
export interface SegmentDatum {
  label: string;
  value: number;
  percentage: number;
}

export type ChartExportDescriptor = { kind: "echarts"; instance: EChartsType } | { kind: "segments"; segments: SegmentDatum[] };

export interface ExportableChart {
  title: string;
  descriptor: ChartExportDescriptor;
}

interface ExportOptions {
  projectName?: string;
  backgroundColor?: string;
  pixelRatio?: number;
}

// Title band drawn above every exported figure. The chart's title lives in the surrounding
// `FigureWrapper` DOM (not in the ECharts option / SegmentBar), so it is not part of what
// the figure renders — we paint it on ourselves so the exported image is self-describing.
const TITLE_BAND_HEIGHT = 44;
const TITLE_FONT = "600 16px Inter, sans-serif";
const TITLE_COLOR = "#1e293b"; // type-default (slate-800)
const TEXT_MUTED = "#5e6e82"; // type-muted
const EXPORT_SPLIT_LINE_COLOR = "#e2e8f0"; // slate-200 — gridlines forced light for export, see forceLightTextColors

// Standalone size + spacing for the SegmentBar figures (they have no intrinsic pixel size).
const SEGMENT_WIDTH = 560;
const SEGMENT_PADDING_X = 24;
const SEGMENT_BAR_HEIGHT = 10;
const SEGMENT_BAR_GAP = 2;
const SEGMENT_BAR_MARGIN_TOP = 12;
const SEGMENT_LEGEND_MARGIN_TOP = 20;
const SEGMENT_LEGEND_ROW_HEIGHT = 26;
const SEGMENT_BOTTOM_PADDING = 20;
const SEGMENT_DOT = 8;
const SEGMENT_LEGEND_FONT = "14px Inter, sans-serif";

// Hex mirror of SegmentBar.vue's PALETTE (Tailwind tokens → literal hex, since canvas
// cannot read Tailwind classes). Keep in sync with SegmentBar's PALETTE order.
const SEGMENT_PALETTE = [
  "#2036ff", // app-blue-600
  "#a072da", // app-purple-500
  "#6aae34", // app-green-500
  "#ef9f11", // app-yellow-500
  "#f77372", // app-red-400
  "#94a3b8", // slate-400
  "#6e93ff", // app-blue-400
  "#d5c1f1", // app-purple-300
];

// A chart's resolved option now bakes in whatever theme is on screen, so force text/gridline
// fields back to light for export — leaving per-series brand fills untouched.
type EChartsOptionRecord = Record<string, unknown>;

function forceLightAxisColors(axis: EChartsOptionRecord) {
  const axisLabel = axis.axisLabel as EChartsOptionRecord | undefined;
  if (axisLabel) {
    axisLabel.color = TITLE_COLOR;
    const textStyle = axisLabel.textStyle as EChartsOptionRecord | undefined;
    if (textStyle) textStyle.color = TITLE_COLOR;
  }
  const axisLineStyle = (axis.axisLine as EChartsOptionRecord | undefined)?.lineStyle as EChartsOptionRecord | undefined;
  if (axisLineStyle) axisLineStyle.color = TITLE_COLOR;
  const splitLineStyle = (axis.splitLine as EChartsOptionRecord | undefined)?.lineStyle as EChartsOptionRecord | undefined;
  if (splitLineStyle) splitLineStyle.color = EXPORT_SPLIT_LINE_COLOR;
  const nameTextStyle = axis.nameTextStyle as EChartsOptionRecord | undefined;
  if (nameTextStyle) nameTextStyle.color = TITLE_COLOR;
}

function forceLightTextColors(option: EChartsOptionRecord) {
  const textStyle = option.textStyle as EChartsOptionRecord | undefined;
  if (textStyle) textStyle.color = TITLE_COLOR;

  const axes = ([] as EChartsOptionRecord[]).concat(
    (option.xAxis as EChartsOptionRecord | EChartsOptionRecord[] | undefined) ?? [],
    (option.yAxis as EChartsOptionRecord | EChartsOptionRecord[] | undefined) ?? []
  );
  axes.forEach(forceLightAxisColors);

  const legends = ([] as EChartsOptionRecord[]).concat(
    (option.legend as EChartsOptionRecord | EChartsOptionRecord[] | undefined) ?? []
  );
  legends.forEach((legend) => {
    const legendTextStyle = legend.textStyle as EChartsOptionRecord | undefined;
    if (legendTextStyle) legendTextStyle.color = TITLE_COLOR;
  });

  const series = (option.series as EChartsOptionRecord[] | undefined) ?? [];
  series.forEach((entry) => {
    const seriesLabel = entry.label as EChartsOptionRecord | undefined;
    if (seriesLabel) seriesLabel.color = TITLE_COLOR;
    const data = (entry.data as EChartsOptionRecord[] | undefined) ?? [];
    data.forEach((datum) => {
      const dataLabel = datum?.label as EChartsOptionRecord | undefined;
      if (dataLabel) dataLabel.color = TITLE_COLOR;
    });
  });
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load chart image for export"));
    image.src = source;
  });
}

// Shorten text with an ellipsis so it never overflows the given width.
function truncateToWidth(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (context.measureText(text).width <= maxWidth) return text;

  const ellipsis = "…";
  let truncated = text;
  while (truncated.length > 1 && context.measureText(truncated + ellipsis).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + ellipsis;
}

function drawTitle(context: CanvasRenderingContext2D, title: string, width: number, paddingX: number) {
  context.fillStyle = TITLE_COLOR;
  context.font = TITLE_FONT;
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(truncateToWidth(context, title, width - paddingX * 2), paddingX, TITLE_BAND_HEIGHT / 2);
}

function fillRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  color: string
) {
  const clampedRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + clampedRadius, y);
  context.arcTo(x + width, y, x + width, y + height, clampedRadius);
  context.arcTo(x + width, y + height, x, y + height, clampedRadius);
  context.arcTo(x, y + height, x, y, clampedRadius);
  context.arcTo(x, y, x + width, y, clampedRadius);
  context.closePath();
  context.fillStyle = color;
  context.fill();
}

function createCanvas(width: number, height: number, backgroundColor: string, pixelRatio: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * pixelRatio);
  canvas.height = Math.round(height * pixelRatio);
  const context = canvas.getContext("2d");
  if (!context) return null;
  // Work in logical (CSS) pixels; the scale bakes in the pixel ratio for a crisp export.
  context.scale(pixelRatio, pixelRatio);
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, width, height);
  return { canvas, context };
}

// Render an ECharts figure to a raster PNG, then composite it beneath a title band.
// Compositing on a separate canvas keeps the title independent of the chart's internal
// grid/legend layout, so it works for every chart type and can never overlap the plot.
async function echartsToPngDataUrl(
  instance: EChartsType,
  title: string,
  backgroundColor: string,
  pixelRatio: number
): Promise<string> {
  const width = instance.getWidth();
  const height = instance.getHeight();

  const container = document.createElement("div");
  // Pass explicit dimensions: the container is detached from the document, so ECharts
  // cannot measure it and would otherwise render at 0×0.
  const offscreen = echarts.init(container, null, { renderer: "canvas", width, height });

  let chartDataUrl: string;
  try {
    const option = instance.getOption();
    // Disable animation so the first (and only) painted frame is the finished chart —
    // getDataURL is synchronous and would otherwise capture a mid-animation frame.
    (option as { animation?: boolean }).animation = false;
    // Force it back to light so an export taken in dark mode still reads on this file's fixed white canvas.
    forceLightTextColors(option as EChartsOptionRecord);
    offscreen.setOption(option);
    chartDataUrl = offscreen.getDataURL({ type: "png", pixelRatio, backgroundColor });
  } finally {
    offscreen.dispose();
  }

  const chartImage = await loadImage(chartDataUrl);

  const created = createCanvas(width, height + TITLE_BAND_HEIGHT, backgroundColor, pixelRatio);
  if (!created) return chartDataUrl; // no 2D context — fall back to the untitled chart

  drawTitle(created.context, title, width, 16);
  created.context.drawImage(chartImage, 0, TITLE_BAND_HEIGHT, width, height);

  return created.canvas.toDataURL("image/png");
}

// Draw a SegmentBar figure (composition bar + legend) beneath a title band, mirroring
// SegmentBar.vue: drop zero values, sort descending, cycle the palette.
function segmentsToPngDataUrl(rawSegments: SegmentDatum[], title: string, backgroundColor: string, pixelRatio: number): string {
  const segments = rawSegments
    .filter((segment) => (segment?.value ?? 0) > 0)
    .sort((first, second) => second.value - first.value)
    .map((segment, index) => ({ ...segment, color: SEGMENT_PALETTE[index % SEGMENT_PALETTE.length] }));

  const width = SEGMENT_WIDTH;
  const legendTop = TITLE_BAND_HEIGHT + SEGMENT_BAR_MARGIN_TOP + SEGMENT_BAR_HEIGHT + SEGMENT_LEGEND_MARGIN_TOP;
  const height = legendTop + segments.length * SEGMENT_LEGEND_ROW_HEIGHT + SEGMENT_BOTTOM_PADDING;

  const created = createCanvas(width, height, backgroundColor, pixelRatio);
  if (!created) throw new Error("Failed to acquire a 2D context for the segment export");
  const { canvas, context } = created;

  drawTitle(context, title, width, SEGMENT_PADDING_X);

  // Composition bar — each segment's width is its share of the track (minus inter-segment gaps).
  const trackWidth = width - SEGMENT_PADDING_X * 2;
  const usableWidth = trackWidth - SEGMENT_BAR_GAP * Math.max(0, segments.length - 1);
  const barY = TITLE_BAND_HEIGHT + SEGMENT_BAR_MARGIN_TOP;
  let barX = SEGMENT_PADDING_X;
  for (const segment of segments) {
    const segmentWidth = (segment.percentage / 100) * usableWidth;
    if (segmentWidth > 0) {
      fillRoundedRect(context, barX, barY, segmentWidth, SEGMENT_BAR_HEIGHT, SEGMENT_BAR_HEIGHT / 2, segment.color);
    }
    barX += segmentWidth + SEGMENT_BAR_GAP;
  }

  // Legend rows — color dot · label (left) … value · percentage (right).
  const labelX = SEGMENT_PADDING_X + SEGMENT_DOT + 10;
  const valueX = width - SEGMENT_PADDING_X;
  context.font = SEGMENT_LEGEND_FONT;
  segments.forEach((segment, index) => {
    const rowCenterY = legendTop + index * SEGMENT_LEGEND_ROW_HEIGHT + SEGMENT_LEGEND_ROW_HEIGHT / 2;

    context.fillStyle = segment.color;
    context.beginPath();
    context.arc(SEGMENT_PADDING_X + SEGMENT_DOT / 2, rowCenterY, SEGMENT_DOT / 2, 0, Math.PI * 2);
    context.fill();

    const valueText = `${format.numberWithDecimals(segment.value)}  ·  ${format.numberWithDecimals(segment.percentage, 1)}%`;
    context.fillStyle = TEXT_MUTED;
    context.textAlign = "right";
    context.textBaseline = "middle";
    context.fillText(valueText, valueX, rowCenterY);

    const valueWidth = context.measureText(valueText).width;
    context.fillStyle = TITLE_COLOR;
    context.textAlign = "left";
    context.fillText(truncateToWidth(context, segment.label, valueX - valueWidth - 16 - labelX), labelX, rowCenterY);
  });

  return canvas.toDataURL("image/png");
}

function descriptorToPngDataUrl(
  descriptor: ChartExportDescriptor,
  title: string,
  backgroundColor: string,
  pixelRatio: number
): Promise<string> {
  if (descriptor.kind === "echarts") {
    return echartsToPngDataUrl(descriptor.instance, title, backgroundColor, pixelRatio);
  }
  return Promise.resolve(segmentsToPngDataUrl(descriptor.segments, title, backgroundColor, pixelRatio));
}

// Strip characters that are illegal in filenames on common filesystems.
function sanitizeFilename(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export the given figures as individual PNG files, one download per figure. Each image
 * includes the figure's title in a band above it. Returns the number exported.
 */
export async function exportChartsAsImages(charts: ExportableChart[], options: ExportOptions = {}): Promise<number> {
  const { projectName = "dashboard", backgroundColor = "#ffffff", pixelRatio = 2 } = options;
  const datePart = format.date(new Date());
  const safeProjectName = sanitizeFilename(projectName) || "dashboard";

  // Make sure the title font is ready before we paint to canvas, otherwise the first
  // export can fall back to a system font. Best-effort — never block the export on it.
  try {
    await document.fonts?.load(TITLE_FONT);
  } catch {
    // Font loading is non-critical; canvas will fall back to sans-serif if unavailable.
  }

  let exported = 0;
  for (const chart of charts) {
    const dataUrl = await descriptorToPngDataUrl(chart.descriptor, chart.title, backgroundColor, pixelRatio);
    const filename = `${safeProjectName} - ${sanitizeFilename(chart.title)} - ${datePart}.png`;
    triggerDownload(dataUrl, filename);
    exported += 1;
    // Small stagger so browsers don't coalesce or block several rapid downloads.
    if (charts.length > 1) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  return exported;
}
